import { useMemo } from 'preact/hooks';
import type { SpotPosition } from '../../services/api.js';

interface AllocationBarProps {
  positions: SpotPosition[];
  totalValueUsd: number;
}

interface Slice {
  key: string;
  symbol: string;
  valueUsd: number;
  pct: number;
  isFutures: boolean;
}

const MAX_LEGEND = 6;

/** Muted jewel-tone palette tuned for the near-black editorial background.
 *  Distinct per holding but kept desaturated so it doesn't fight the amber accent. */
const PALETTE = [
  '#FDB022', // amber (brand)
  '#2DD4BF', // teal
  '#A78BFA', // violet
  '#60A5FA', // blue
  '#4ADE80', // green
  '#F472B6', // rose
  '#FB923C', // orange
  '#38BDF8', // sky
  '#C084FC', // purple
  '#FACC15', // gold
] as const;

const colorFor = (index: number): string => PALETTE[index % PALETTE.length];

/**
 * Editorial allocation bar — each holding's share of total portfolio value as a
 * single segmented hairline bar + a compact legend. Pure presentational; all data
 * comes from the already-enriched snapshot.
 */
export function AllocationBar({ positions, totalValueUsd }: AllocationBarProps) {
  const slices = useMemo<Slice[]>(() => {
    if (totalValueUsd <= 0) return [];
    return positions
      .filter((p) => p.valueUsd > 0)
      .map((p) => ({
        key: p.id,
        symbol: p.symbol.toUpperCase(),
        valueUsd: p.valueUsd,
        pct: (p.valueUsd / totalValueUsd) * 100,
        isFutures: p.isFutures,
      }))
      .sort((a, b) => b.valueUsd - a.valueUsd);
  }, [positions, totalValueUsd]);

  if (slices.length < 2) return null;

  const legend = slices.slice(0, MAX_LEGEND);
  const restPct = slices.slice(MAX_LEGEND).reduce((s, x) => s + x.pct, 0);

  return (
    <section class="spot-alloc" aria-label="Распределение портфеля">
      <div class="spot-alloc-head">
        <span class="spot-alloc-title">Распределение портфеля</span>
        <span class="spot-alloc-count">{slices.length} поз.</span>
      </div>

      <div class="spot-alloc-bar" role="img" aria-label="Доли позиций в портфеле">
        {slices.map((s, i) => (
          <div
            key={s.key}
            class={`spot-alloc-seg ${s.isFutures ? 'fut' : ''}`}
            style={{ width: `${s.pct}%`, background: colorFor(i) }}
            title={`${s.symbol} · ${s.pct.toFixed(1)}% · $${s.valueUsd.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
          />
        ))}
      </div>

      <div class="spot-alloc-legend">
        {legend.map((s, i) => (
          <div class="spot-alloc-legend-item" key={s.key}>
            <span class={`spot-alloc-dot ${s.isFutures ? 'fut' : ''}`} style={{ background: colorFor(i) }} />
            <span class="spot-alloc-sym">{s.symbol}</span>
            <span class="spot-alloc-pct">{s.pct.toFixed(1)}%</span>
          </div>
        ))}
        {restPct > 0.05 && (
          <div class="spot-alloc-legend-item">
            <span class="spot-alloc-dot rest" />
            <span class="spot-alloc-sym">проч.</span>
            <span class="spot-alloc-pct">{restPct.toFixed(1)}%</span>
          </div>
        )}
      </div>
    </section>
  );
}
